import { Components, Inputs } from "@thisgorilla/primate-ds";
import { useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { hooks } from "../../../hooks";
import { useAppSelector } from "../../../redux";
import { DROPDOWN_OVER_MODAL_Z_INDEX } from "../../../utils";
import { Emoji } from "../../Emoji/EmojiIcon";
import SliderDropdownProps from "./SliderDropdown";

const SliderDropdown = ({
  onValueChange,
  selectedId,
  otherSelectedId,
  scrollId = "",
}: SliderDropdownProps.IProps) => {
  const size = hooks.useBodySize();
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const { typography } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const scroll = hooks.useScroll(scrollId);
  const { emojis } = useAppSelector((state) => state.me);
  const [highlightedOptionIndex, setHighlightedOptionIndex] = useState(0);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  const menuHeight = dropdownRef.current
    ? (dropdownRef.current as Element).clientHeight
    : 0;

  hooks.useClickOutside({
    ref: dropdownRef,
    setState: setIsOpen,
    excludedRef: containerRef,
  });

  useEffect(() => {
    setIsOpen(false);
  }, [scroll]);

  useEffect(() => {
    if (containerRef?.current) {
      const wrapperRect = (
        containerRef?.current as Element
      )?.getBoundingClientRect();

      setMenuPosition({
        top: wrapperRect?.bottom,
        left: wrapperRect?.left,
        width: wrapperRect?.width,
        height: wrapperRect?.height,
      });
    }
  }, [isOpen, containerRef, size]);

  const calculateBlocks = (containerWidth: number): number => {
    if (containerWidth < 32) {
      return 0;
    }
    return 1 + calculateBlocks(containerWidth - 40);
  };

  const keyDown = hooks.useDropdownKeyDown({
    handleSelect: (id: string) => onValueChange(id),
    highlightedIndex: highlightedOptionIndex,
    isDropdownOpen: isOpen,
    options: Object.keys(emojis),
    setDropdownOpen: setIsOpen,
    setHighlightedIndex: setHighlightedOptionIndex,
    renderInSigleLine: calculateBlocks(menuPosition.width - 26),
  });

  return (
    <>
      <Container
        onClick={() => {
          setIsOpen(!isOpen);
          setHighlightedOptionIndex(0);
        }}
        tabIndex={0}
        onKeyDown={keyDown}
        $active={isOpen}
        ref={containerRef}
      >
        <EmojiContainer>
          <Emoji type={emojis[selectedId]?.name ?? "none"} />
        </EmojiContainer>
        <div>
          <Components.IconButton
            disabled={false}
            iconType={isOpen ? "ChevronUp" : "ChevronDown"}
            kind="Tertiary"
            onClick={() => {}}
            size="Small"
          />
        </div>
      </Container>
      {isOpen && (
        <MenuContainer
          ref={dropdownRef}
          $menuPosition={menuPosition}
          $menuHeight={menuHeight}
          $positon={
            window.innerHeight - menuPosition.top - menuHeight > 0
              ? "bottom"
              : "top"
          }
        >
          <Components.Menu size="fill" itemGap={16}>
            <Components.Text
              style={{ tokenStyle: typography.Heading.S }}
              fontWeight="font-weight-semibold"
              text="Available emojis"
              lineHeight="line-height-400"
            />
            <EmojiListWrapper>
              {Object.entries(emojis)?.map(([key, { name }], index) => {
                if (key === otherSelectedId) return null;
                return (
                  <EmojiWrapper
                    $selected={key === selectedId}
                    $hover={highlightedOptionIndex === index}
                    key={index}
                    onClick={() => {
                      onValueChange(key);
                      setIsOpen(false);
                    }}
                    onMouseEnter={() => {
                      setHighlightedOptionIndex(index);
                    }}
                  >
                    <Emoji type={name} />
                  </EmojiWrapper>
                );
              })}
            </EmojiListWrapper>
          </Components.Menu>
        </MenuContainer>
      )}
    </>
  );
};

export default SliderDropdown;

const Container = styled.div<{
  $active: boolean;
}>`
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  cursor: pointer;
  border: ${({ theme }) => theme.border_width["border-width-thin"].value}rem
    solid;
  outline: none;
  gap: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem
    ${({ theme }) => theme.spacing["spacing-2"].value}rem
    ${({ theme }) => theme.spacing["spacing-2"].value}rem
    ${({ theme }) => theme.spacing["spacing-4"].value}rem;
  border-radius: ${({ theme }) => theme.radius["radius-xxs"].value}rem;
  ${({ $active }) =>
    $active
      ? Inputs.CommonField.CommonFieldStyle.inputActivecss
      : Inputs.CommonField.getInputCss("Normal")}
`;

const EmojiContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const EmojiListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
`;
const EmojiWrapper = styled.div<{ $selected: boolean; $hover: boolean }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius["radius-xxs"].value}rem;
  cursor: pointer;
  background: ${({ theme, $selected, $hover }) =>
    $selected || $hover
      ? theme.Color.elevation.surface["surface-primary-hover"].value
      : "transparent"};
  border: ${({ theme, $selected }) =>
    $selected &&
    "1px solid" + theme.Color.border["ui-border-emphasize-brand"].value};
  &:hover {
    background: ${({ theme }) =>
      theme.Color.elevation.surface["surface-primary-hover"].value};
  }
`;

const MenuContainer = styled.div<{
  $menuPosition: { top: number; left: number; width: number; height: number };
  $positon: "top" | "bottom";
  $menuHeight: number;
}>`
  position: fixed;
  top: ${({ $menuPosition, $positon, $menuHeight }) =>
    $positon === "top"
      ? $menuPosition.top - $menuPosition.height - $menuHeight - 8
      : $menuPosition.top + 8}px;
  left: ${({ $menuPosition }) => $menuPosition.left + 1}px;
  z-index: ${DROPDOWN_OVER_MODAL_Z_INDEX};
  width: ${({ $menuPosition }) => $menuPosition.width - 2}px;
  border-radius: ${({ theme }) => theme.radius["radius-xs"].value}rem;
  background: ${({ theme }) =>
    theme.Color.elevation.surface["surface-primary"].value};

  & > div {
    padding: ${({ theme }) => theme.spacing["spacing-5"].value}rem
      ${({ theme }) => theme.spacing["spacing-3"].value}rem;
  }
`;
